import { REFERENCE_NODE } from '../consts/text.js';
import type {
  AffineInlineEditor,
  AffineTextAttributes,
} from '../types/index.js';
import type {
  BlockSnapshot,
  DeltaInsert,
  SliceSnapshot,
} from '@blocksuite/store';
import type { InlineRange } from '@blocksuite/std/inline';

import { splitTextByUrl, type UrlTextSegment } from './url.js';

export const AFFINE_INLINE_CLIPBOARD_MIME = 'application/x-affine-inline-delta';

const linkedDocFlavours = new Set([
  'affine:embed-linked-doc',
  'affine:embed-synced-doc',
]);

type UrlPasteInlineEditor = Pick<
  AffineInlineEditor,
  'insertText' | 'setInlineRange'
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getTextDeltas(
  props: Record<string, unknown>
): DeltaInsert<AffineTextAttributes>[] {
  const text = props['text'];
  if (!isRecord(text) || !Array.isArray(text['delta'])) {
    return [];
  }

  return normalizeInlineDeltas(text['delta']);
}

function normalizeInlineDeltas(
  value: unknown
): DeltaInsert<AffineTextAttributes>[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap(delta => {
    if (!isRecord(delta) || typeof delta['insert'] !== 'string') {
      return [];
    }

    const attributes = isRecord(delta['attributes'])
      ? (delta['attributes'] as AffineTextAttributes)
      : undefined;
    return [
      {
        insert: delta['insert'],
        ...(attributes ? { attributes } : {}),
      },
    ];
  });
}

function getLinkedDocDelta(
  snapshot: BlockSnapshot
): DeltaInsert<AffineTextAttributes> | null {
  if (!linkedDocFlavours.has(snapshot.flavour) || !isRecord(snapshot.props)) {
    return null;
  }

  const pageId = snapshot.props['pageId'];
  if (typeof pageId !== 'string' || pageId.length === 0) {
    return null;
  }

  const reference: NonNullable<AffineTextAttributes['reference']> = {
    type: 'LinkedPage',
    pageId,
  };
  const params = snapshot.props['params'];
  const title = snapshot.props['title'];
  const description = snapshot.props['description'];

  if (isRecord(params)) {
    reference.params = params;
  }
  if (typeof title === 'string') {
    reference.title = title;
  }
  if (typeof description === 'string') {
    reference.description = description;
  }

  return {
    insert: REFERENCE_NODE,
    attributes: { reference },
  };
}

/**
 * Converts a copied block tree into inline content. Linked-document cards have
 * no text delta, so they are represented by their canonical inline reference.
 */
export function inlineDeltasFromBlockSnapshot(
  snapshot: BlockSnapshot
): DeltaInsert<AffineTextAttributes>[] {
  const linkedDocDelta = getLinkedDocDelta(snapshot);
  if (linkedDocDelta) {
    return [linkedDocDelta];
  }

  const props = isRecord(snapshot.props) ? snapshot.props : {};
  return [
    ...getTextDeltas(props),
    ...(snapshot.children?.flatMap(inlineDeltasFromBlockSnapshot) ?? []),
  ];
}

export function inlineDeltasFromSliceSnapshot(
  snapshot: SliceSnapshot
): DeltaInsert<AffineTextAttributes>[] {
  return snapshot.content.flatMap(inlineDeltasFromBlockSnapshot);
}

export function inlineDeltasFromClipboardSnapshot(
  serializedSnapshot: unknown
): DeltaInsert<AffineTextAttributes>[] {
  if (typeof serializedSnapshot !== 'string') {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(serializedSnapshot);
    if (!isRecord(parsed) || !isRecord(parsed['snapshot'])) {
      return [];
    }

    const snapshot = parsed['snapshot'];
    if (!Array.isArray(snapshot['content'])) {
      return [];
    }

    return inlineDeltasFromSliceSnapshot(snapshot as SliceSnapshot);
  } catch {
    return [];
  }
}

export function inlineDeltasFromSerializedDeltas(
  serializedDeltas: unknown
): DeltaInsert<AffineTextAttributes>[] {
  if (typeof serializedDeltas !== 'string') {
    return [];
  }

  try {
    return normalizeInlineDeltas(JSON.parse(serializedDeltas));
  } catch {
    return [];
  }
}

export function sliceInlineDeltas(
  deltas: DeltaInsert<AffineTextAttributes>[],
  begin: number,
  end: number
): DeltaInsert<AffineTextAttributes>[] {
  if (begin < 0 || end <= begin) {
    return [];
  }

  const result: DeltaInsert<AffineTextAttributes>[] = [];
  let offset = 0;
  for (const delta of deltas) {
    const deltaStart = offset;
    const deltaEnd = offset + delta.insert.length;
    const start = Math.max(begin, deltaStart);
    const finish = Math.min(end, deltaEnd);
    if (finish > start) {
      result.push({
        ...delta,
        insert: delta.insert.slice(start - deltaStart, finish - deltaStart),
      });
    }
    offset = deltaEnd;
    if (offset >= end) {
      break;
    }
  }
  return result;
}

export function insertInlineDeltas(
  inlineEditor: Pick<AffineInlineEditor, 'insertText' | 'setInlineRange'>,
  inlineRange: InlineRange,
  deltas: DeltaInsert<AffineTextAttributes>[]
) {
  let index = inlineRange.index;
  deltas.forEach((delta, deltaIndex) => {
    inlineEditor.insertText(
      {
        index,
        length: deltaIndex === 0 ? inlineRange.length : 0,
      },
      delta.insert,
      delta.attributes
    );
    index += delta.insert.length;
  });
  inlineEditor.setInlineRange({ index, length: 0 });
}

export function inlineDeltasFromClipboardData(
  clipboardData: DataTransfer
): DeltaInsert<AffineTextAttributes>[] {
  return inlineDeltasFromSerializedDeltas(
    clipboardData.getData(AFFINE_INLINE_CLIPBOARD_MIME)
  );
}

export function serializeInlineDeltas(
  deltas: DeltaInsert<AffineTextAttributes>[]
) {
  return JSON.stringify(deltas);
}

export function analyzeTextForUrlPaste(text: string) {
  const segments = splitTextByUrl(text);
  const firstSegment = segments[0];
  const singleUrl =
    segments.length === 1 && firstSegment?.link && firstSegment.text === text
      ? firstSegment.link
      : undefined;
  return {
    segments,
    singleUrl,
  };
}

export function insertUrlTextSegments(
  inlineEditor: UrlPasteInlineEditor,
  inlineRange: InlineRange,
  segments: UrlTextSegment[]
) {
  let index = inlineRange.index;
  let replacedSelection = false;
  segments.forEach(segment => {
    if (!segment.text) return;
    const attributes: AffineTextAttributes | undefined = segment.link
      ? { link: segment.link }
      : undefined;
    inlineEditor.insertText(
      {
        index,
        length: replacedSelection ? 0 : inlineRange.length,
      },
      segment.text,
      attributes
    );
    replacedSelection = true;
    index += segment.text.length;
  });
  inlineEditor.setInlineRange({
    index,
    length: 0,
  });
}
